import { useState } from 'react';
import { Button } from '../ui/button';
import { sarApi } from '../../lib/api';
import { toast } from 'sonner';

/**
 * SAROutcomeFlags
 * ---------------
 * Two toggles so the user can record what happened with a request: a
 * refusal, or records arriving with redactions. They feed organisation-level
 * patterns; the user's own data is never shared.
 *
 * Nothing has necessarily gone wrong (or right) on a request that has just
 * been sent, so this only appears once there is actually something to
 * report: the organisation has replied at least once, the deadline has
 * passed, or a flag is already set. A freshly-sent request with an empty
 * thread has no "what happened" to ask about yet.
 *
 * Each toggle is fire-and-forget: the click immediately POSTs the flag and
 * the canonical-org stats refresh in the background.
 */
const FLAGS = [
  {
    key: 'refusal',
    field: 'refused_at',
    api: 'flagRefusal',
    onLabel: 'They refused',
    offLabel: 'They refused to release my records',
    onToast: 'Recorded: they refused.',
    offToast: 'Refusal cleared.',
  },
  {
    key: 'redactions',
    field: 'redactions_flagged',
    api: 'flagRedactions',
    onLabel: 'Parts were blacked out',
    offLabel: 'Parts of my records were blacked out',
    onToast: 'Recorded: parts were blacked out.',
    offToast: 'Redactions cleared.',
  },
];

const RELEVANT_STATUSES = ['overdue', 'partial', 'complete', 'escalated_ico'];

const SAROutcomeFlags = ({ sar, emailThread = [], onUpdate }) => {
  const [busy, setBusy] = useState(null);
  if (!sar) return null;

  const isOn = (field) => !!sar?.[field];

  const hasIncomingReply = emailThread.some((e) => e.direction === 'incoming');
  const status = sar.effective_status || sar.status;
  const relevant = hasIncomingReply
    || RELEVANT_STATUSES.includes(status)
    || FLAGS.some((flag) => isOn(flag.field));
  if (!relevant) return null;

  const toggle = async (flag) => {
    setBusy(flag.key);
    try {
      const next = !isOn(flag.field);
      await sarApi[flag.api](sar.id, next);
      toast.success(next ? flag.onToast : flag.offToast);
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that. Try again in a moment.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="rounded-lg bg-ground px-6 py-5" data-testid="sar-outcome-flags">
      <h2 className="text-[16px] font-semibold text-ink">What happened with this request</h2>
      <p className="mt-1 text-sm text-ink-55 max-w-[60ch]">
        Tick anything that applies. It helps show patterns across organisations. Your own records
        are never shared.
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {FLAGS.map((flag) => {
          const on = isOn(flag.field);
          return (
            <Button
              key={flag.key}
              variant={on ? 'default' : 'secondary'}
              size="sm"
              disabled={busy === flag.key}
              onClick={() => toggle(flag)}
              className={`justify-start whitespace-normal text-left ${on ? '' : 'bg-surface'}`}
              data-testid={`sar-flag-${flag.key}`}
              aria-pressed={on}
            >
              {on ? flag.onLabel : flag.offLabel}
            </Button>
          );
        })}
      </div>
    </section>
  );
};

export default SAROutcomeFlags;
